const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** "100.00" -> "$100.00". Amounts arrive as decimal strings; this is display-only. */
export function formatUsd(amount: string, currency = "USD"): string {
  const n = Number(amount);
  if (!Number.isFinite(n) || currency.toUpperCase() !== "USD") return `${amount} ${currency.toUpperCase()}`;
  return usd.format(n);
}

const usdCompact = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

/** Chart axis labels: "$0", "$950", "$1.2K". */
export function formatUsdCompact(amount: number): string {
  return usdCompact.format(amount);
}

const percent = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1 });

/** 0.9734 -> "97.3%". */
export function formatPercent(ratio: number): string {
  return percent.format(ratio);
}

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : dateTime.format(d);
}

const thisYear = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
const otherYear = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

/**
 * A table's date, in the same style as formatDateTime but shorter: "Nov 3, 2:29 PM" this year,
 * "Nov 3, 2025" for an earlier one (the full time is the cell's title).
 */
export function formatTableDate(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (d.getFullYear() === now.getFullYear() ? thisYear : otherYear).format(d);
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-10-28 06:27:14" in local time: fixed width, for ledgers that are read and compared. */
export function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Series dates are UTC calendar days ("2026-10-28"); format them in UTC so they never shift a day.
const shortDay = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
const longDay = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeZone: "UTC" });

export function formatDay(date: string, style: "short" | "long" = "short"): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return (style === "short" ? shortDay : longDay).format(d);
}

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 86400],
  ["month", 30 * 86400],
  ["week", 7 * 86400],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

/** "3 minutes ago", "yesterday", "in 2 hours"; "just now" under a minute. */
export function formatRelative(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const seconds = Math.round((t - now) / 1000);
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) return relative.format(Math.trunc(seconds / size), unit);
  }
  return "just now";
}

/** Countdown text for a number of milliseconds: "4:59", "1:02:03", "2d 4h". */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (days > 0) return `${days}d ${h}h`;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function formatSeconds(seconds: number): string {
  if (seconds % 86400 === 0) return `${seconds / 86400} day${seconds === 86400 ? "" : "s"}`;
  if (seconds % 3600 === 0) return `${seconds / 3600} hour${seconds === 3600 ? "" : "s"}`;
  if (seconds % 60 === 0) return `${seconds / 60} minutes`;
  return `${seconds} seconds`;
}

/** 0x1234…abcd */
export function shortHash(value: string, head = 6, tail = 4): string {
  return value.length <= head + tail + 1 ? value : `${value.slice(0, head)}…${value.slice(-tail)}`;
}

const NETWORK_LABELS: Record<string, string> = {
  base: "Base",
  ethereum: "Ethereum",
  arbitrum: "Arbitrum",
  optimism: "OP Mainnet",
  polygon: "Polygon",
  bsc: "BNB Smart Chain",
  avalanche: "Avalanche",
  tron: "TRON",
  solana: "Solana",
  bitcoin: "Bitcoin",
};

export function networkLabel(slug: string): string {
  return NETWORK_LABELS[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1);
}

export function bpsToPercent(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`;
}
